/**
 * CRM Custom Claims Model
 *
 * Definiert die Custom Claims fuer Firebase Authentication im CRM-System.
 * Diese Claims erweitern die bestehenden POS Custom Claims um CRM-spezifische Rollen.
 *
 * WICHTIG: Bestehende Claims (tenantId, role) bleiben unveraendert!
 */

/**
 * CRM-spezifische Rollen
 */
export type CrmRole = 'crm_admin' | 'super_admin';

/**
 * CRM-Permissions
 * Granulare Berechtigungen fuer CRM-Funktionen
 */
export type CrmPermission =
  | 'crm:tenants:read'       // Alle Tenants sehen
  | 'crm:tenants:write'      // Tenants erstellen/bearbeiten
  | 'crm:tenants:delete'     // Tenants loeschen (nur super_admin)
  | 'crm:tenants:suspend'    // Tenants sperren/entsperren
  | 'crm:invoices:read'      // Rechnungen sehen
  | 'crm:invoices:write'     // Rechnungen erstellen
  | 'crm:support:read'       // Support-Tickets sehen
  | 'crm:support:write'      // Support-Tickets beantworten
  | 'crm:audit:read'         // Audit-Logs sehen (nur super_admin)
  | 'crm:stats:read'         // Statistiken sehen
  | 'crm:users:manage';      // CRM-User verwalten (nur super_admin)

/**
 * Custom Claims Interface
 * Erweitert bestehende Firebase Custom Claims um CRM-spezifische Felder
 */
export interface CrmCustomClaims {
  // ===== BESTEHENDE CLAIMS (NICHT AENDERN!) =====
  /** Tenant-ID fuer tenant_admin/tenant_user */
  tenantId?: string;

  /** Rolle im Tenant-System */
  role?: 'owner' | 'admin' | 'manager' | 'cashier' | 'viewer';

  /** Erlaubte Location-IDs (Multi-Location Support) */
  allowedLocationIds?: string[];

  // ===== NEUE CRM-SPEZIFISCHE CLAIMS =====
  /** CRM-Rolle (fuer CRM-Backend-Zugriff) */
  crmRole?: CrmRole;

  /** CRM-Permissions (granulare Berechtigungen) */
  crmPermissions?: CrmPermission[];

  // ===== TENANT APPROVAL & IMPERSONATION =====
  /** Tenant ist vom CRM-Admin freigeschaltet (nur fuer Tenant-Owner) */
  approved?: boolean;

  /** SuperAdmin darf Tenants impersonieren */
  canImpersonate?: boolean;

  /** Aktuell impersonierter Tenant (falls SuperAdmin als Tenant eingeloggt) */
  impersonatedTenantId?: string;

  // ===== PASSWORD INITIALIZATION =====
  /** Passwort wurde initialisiert (false = erster Login, true = Passwort gesetzt) */
  passwordInitialized?: boolean;
}

/**
 * Rollen-Permissions Matrix
 * Definiert welche Permissions jede CRM-Rolle standardmaessig erhaelt
 */
export const CRM_ROLE_PERMISSIONS: Record<CrmRole, CrmPermission[]> = {
  crm_admin: [
    'crm:tenants:read',
    'crm:tenants:write',
    'crm:tenants:suspend',
    'crm:invoices:read',
    'crm:invoices:write',
    'crm:support:read',
    'crm:support:write',
    'crm:stats:read'
  ],
  super_admin: [
    'crm:tenants:read',
    'crm:tenants:write',
    'crm:tenants:delete',
    'crm:tenants:suspend',
    'crm:invoices:read',
    'crm:invoices:write',
    'crm:support:read',
    'crm:support:write',
    'crm:audit:read',
    'crm:stats:read',
    'crm:users:manage'
  ]
};

/**
 * Type Guard: Prueft ob User eine CRM-Rolle hat
 */
export function hasCrmRole(claims: CrmCustomClaims | null | undefined): boolean {
  return !!claims?.crmRole;
}

/**
 * Type Guard: Prueft ob User CRM-Admin ist (inkl. super_admin)
 */
export function isCrmAdmin(claims: CrmCustomClaims | null | undefined): boolean {
  const crmRole = claims?.crmRole;
  return crmRole === 'crm_admin' || crmRole === 'super_admin';
}

/**
 * Type Guard: Prueft ob User Super-Admin ist
 */
export function isSuperAdmin(claims: CrmCustomClaims | null | undefined): boolean {
  return claims?.crmRole === 'super_admin';
}

/**
 * Type Guard: Prueft ob User eine spezifische CRM-Permission hat
 */
export function hasCrmPermission(
  claims: CrmCustomClaims | null | undefined,
  permission: CrmPermission
): boolean {
  return claims?.crmPermissions?.includes(permission) ?? false;
}

/**
 * Helper: Ermittelt alle Permissions fuer eine CRM-Rolle
 */
export function getCrmRolePermissions(role: CrmRole): CrmPermission[] {
  return CRM_ROLE_PERMISSIONS[role] || [];
}

/**
 * Type Guard: Prueft ob Tenant-User freigeschaltet ist
 */
export function isTenantApproved(claims: CrmCustomClaims | null | undefined): boolean {
  return claims?.approved === true;
}

/**
 * Type Guard: Prueft ob User Impersonation nutzen darf (nur SuperAdmin)
 */
export function canImpersonate(claims: CrmCustomClaims | null | undefined): boolean {
  return claims?.canImpersonate === true && isSuperAdmin(claims);
}

/**
 * Type Guard: Prueft ob User gerade einen Tenant impersoniert
 */
export function isImpersonating(claims: CrmCustomClaims | null | undefined): boolean {
  return !!claims?.impersonatedTenantId;
}

/**
 * Helper: Ermittelt die effektive Tenant-ID (impersoniert oder eigen)
 */
export function getEffectiveTenantId(claims: CrmCustomClaims | null | undefined): string | undefined {
  return claims?.impersonatedTenantId || claims?.tenantId;
}

/**
 * Type Guard: Prueft ob User Passwort bereits initialisiert hat
 */
export function hasPasswordInitialized(claims: CrmCustomClaims | null | undefined): boolean {
  return claims?.passwordInitialized === true;
}

/**
 * Type Guard: Prueft ob User beim ersten Login ist (Passwort noch nicht initialisiert)
 */
export function isFirstLogin(claims: CrmCustomClaims | null | undefined): boolean {
  return claims?.passwordInitialized === false;
}
